import { html } from 'lit';
import { hasRole, sessionStore, waitForSession } from './auth.js';

/** @type {import('@lit-labs/router').Router | undefined} */
let router;

export function registerRouter(instance) {
  router = instance;
}

/**
 * Nawigacja programowa.
 *
 * Router z @lit-labs/router sam przechwytuje kliknięcia w linki i zdarzenie
 * popstate, ale nie wie o wywołaniach history.pushState — stąd te dwie linie
 * zawsze razem.
 *
 * Ścieżki tras są po polsku (`/logowanie`, `/pojedynek`) — to adresy widoczne
 * dla użytkownika, część interfejsu, nie kodu.
 */
export function navigate(path, { replace = false } = {}) {
  if (replace) history.replaceState({}, '', path);
  else history.pushState({}, '', path);

  // goto() dopasowuje wyłącznie ścieżkę — parametr zapytania w argumencie
  // rozwaliłby dopasowanie i każdy adres z `?powrot=` lądowałby na 404.
  // W pasku adresu query zostaje, bo trafiło tam przez pushState.
  router?.goto(new URL(path, location.origin).pathname);
  window.scrollTo({ top: 0 });
}

/** Strażnik: trasa tylko dla zalogowanych o wskazanych rolach. */
function requireRole(...roles) {
  return async () => {
    await waitForSession();
    const { user } = sessionStore.get();

    if (!user) {
      const returnTo = encodeURIComponent(location.pathname + location.search);
      navigate(`/logowanie?powrot=${returnTo}`, { replace: true });
      return false;
    }

    if (roles.length > 0 && !hasRole(user, ...roles)) {
      navigate('/brak-dostepu', { replace: true });
      return false;
    }

    return true;
  };
}

/** Strażnik: trasa tylko dla niezalogowanych (formularze logowania). */
async function guestOnly() {
  await waitForSession();
  if (sessionStore.get().user) {
    navigate('/graj', { replace: true });
    return false;
  }
  return true;
}

/**
 * Widoki są ładowane leniwie — edytor i panel admina nie mają prawa
 * trafić do głównego bundla, bo większość odwiedzających nigdy ich nie otworzy.
 *
 * `/` sprzedaje Quizotekę komuś z zewnątrz, `/graj` jest ekranem startowym
 * zalogowanego. Strona główna zostaje dostępna także z kontem — kto kliknie
 * w nazwę w nagłówku, ma prawo ją zobaczyć — ale nigdzie się na nią nie ląduje:
 * logowanie prowadzi do `/graj`.
 *
 * Publicznych tras do przeglądania katalogu nie ma i mieć nie może: pytanie
 * jest materiałem, z którego losuje się pojedynek, a nie treścią wystawioną
 * na widok. Poza redakcją nikt nie ogląda bazy pytań — gracz styka się
 * z pytaniem dopiero w rozgrywce, po jednym i na czas. Quizu nie ma wcale
 * od migracji 0015, więc `/quizy` nie mogłoby nawet oznaczać czegokolwiek.
 */
export const routes = [
  {
    path: '/',
    enter: async () => {
      await import('../views/qz-home-view.js');
    },
    render: () => html`<qz-home-view></qz-home-view>`,
  },
  {
    path: '/graj',
    enter: async () => {
      if (!(await requireRole()())) return false;
      await import('../views/qz-lobby-view.js');
      return true;
    },
    render: () => html`<qz-lobby-view></qz-lobby-view>`,
  },
  {
    // Bardziej szczegółowa trasa przed `/pojedynek`.
    path: '/pojedynek/:duelId',
    enter: async () => {
      if (!(await requireRole()())) return false;
      await import('../views/qz-duel-view.js');
      return true;
    },
    render: ({ duelId }) => html`<qz-duel-view .duelId=${duelId}></qz-duel-view>`,
  },
  {
    path: '/pojedynek',
    enter: async () => {
      if (!(await requireRole()())) return false;
      await import('../views/qz-matchmaking-view.js');
      return true;
    },
    render: () => html`<qz-matchmaking-view></qz-matchmaking-view>`,
  },
  {
    path: '/znajomi',
    enter: async () => {
      if (!(await requireRole()())) return false;
      await import('../views/qz-friends-view.js');
      return true;
    },
    render: () => html`<qz-friends-view></qz-friends-view>`,
  },
  {
    path: '/logowanie',
    enter: async () => {
      if (!(await guestOnly())) return false;
      await import('../views/qz-auth-view.js');
      return true;
    },
    render: () => html`<qz-auth-view></qz-auth-view>`,
  },
  {
    path: '/profil',
    enter: async () => {
      if (!(await requireRole()())) return false;
      await import('../views/qz-profile-view.js');
      return true;
    },
    render: () => html`<qz-profile-view></qz-profile-view>`,
  },
  {
    // Import przed `/tworz/pytanie/:questionId` nie koliduje, ale przed `/tworz`
    // stać musi — inaczej trasa ogólna zjadłaby dopasowanie.
    path: '/tworz/import',
    enter: async () => {
      if (!(await requireRole('admin')())) return false;
      await import('../views/admin/qz-import-view.js');
      return true;
    },
    render: () => html`<qz-import-view></qz-import-view>`,
  },
  {
    // Bardziej szczegółowa trasa przed `/tworz`. `nowe` w miejscu identyfikatora
    // znaczy „jeszcze nie istnieje": pytanie powstaje dopiero przy zapisie,
    // bo API wymaga treści i dwóch wariantów.
    path: '/tworz/pytanie/:questionId',
    enter: async () => {
      if (!(await requireRole('admin')())) return false;
      await import('../views/qz-question-view.js');
      return true;
    },
    render: ({ questionId }) =>
      html`<qz-question-view .questionId=${questionId}></qz-question-view>`,
  },
  {
    path: '/tworz',
    enter: async () => {
      if (!(await requireRole('admin')())) return false;
      await import('../views/qz-questions-view.js');
      return true;
    },
    render: () => html`<qz-questions-view></qz-questions-view>`,
  },
  {
    // Podstrony przed `/admin`, inaczej zjadłaby dopasowanie.
    path: '/admin/konta',
    enter: async () => {
      if (!(await requireRole('admin')())) return false;
      await import('../views/admin/qz-admin-accounts-view.js');
      return true;
    },
    render: () => html`<qz-admin-accounts-view></qz-admin-accounts-view>`,
  },
  {
    path: '/admin/boty',
    enter: async () => {
      if (!(await requireRole('admin')())) return false;
      await import('../views/admin/qz-admin-bots-view.js');
      return true;
    },
    render: () => html`<qz-admin-bots-view></qz-admin-bots-view>`,
  },
  {
    path: '/admin/kategorie',
    enter: async () => {
      if (!(await requireRole('admin')())) return false;
      await import('../views/admin/qz-admin-categories-view.js');
      return true;
    },
    render: () => html`<qz-admin-categories-view></qz-admin-categories-view>`,
  },
  {
    path: '/admin',
    enter: async () => {
      if (!(await requireRole('admin')())) return false;
      await import('../views/admin/qz-admin-dashboard-view.js');
      return true;
    },
    render: () => html`<qz-admin-dashboard-view></qz-admin-dashboard-view>`,
  },
  {
    path: '/brak-dostepu',
    enter: async () => {
      await import('../views/qz-error-view.js');
    },
    render: () =>
      html`<qz-error-view
        code="403"
        heading="Ta część katalogu jest zamknięta"
        text="Twoja rola nie pozwala tu wejść. Jeśli to pomyłka, poproś administratora o zmianę uprawnień."
      ></qz-error-view>`,
  },
];

export const fallbackRoute = {
  enter: async () => {
    await import('../views/qz-error-view.js');
  },
  render: () =>
    html`<qz-error-view
      code="404"
      heading="Nie ma takiej karty w katalogu"
      text="Adres jest błędny albo strona została usunięta."
    ></qz-error-view>`,
};
